import { useState, useEffect, useRef } from 'react';
import './Hero.css';

const ROLES = [
  'CS Student + UI/UX Engineer',
  'Frontend Developer',
  'Problem Solver',
  'Product-Minded Builder',
];

function TypeWriter({ strings }) {
  const [text, setText] = useState('');
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState('typing'); // typing | pausing | deleting
  const [charIdx, setCharIdx] = useState(0);

  useEffect(() => {
    const current = strings[idx];
    let timeout;
    if (phase === 'typing') {
      if (charIdx < current.length) {
        timeout = setTimeout(() => setCharIdx(c => c + 1), 60);
      } else {
        timeout = setTimeout(() => setPhase('pausing'), 1800);
      }
    } else if (phase === 'pausing') {
      timeout = setTimeout(() => setPhase('deleting'), 400);
    } else {
      if (charIdx > 0) {
        timeout = setTimeout(() => setCharIdx(c => c - 1), 30);
      } else {
        setIdx(i => (i + 1) % strings.length);
        setPhase('typing');
      }
    }
    setText(current.slice(0, charIdx));
    return () => clearTimeout(timeout);
  }, [charIdx, phase, idx, strings]);

  return (
    <span className="typewriter">
      <span className="tw-prefix">const role = </span>
      <span className="tw-value">"{text}"</span>
      <span className="tw-cursor">|</span>
    </span>
  );
}

function CountUp({ target, suffix = '', prefix = '', delay = 0 }) {
  const [val, setVal] = useState(target > 100 ? target - 6 : 0);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        obs.disconnect();
        setTimeout(() => {
          const start = target > 100 ? target - 6 : 0;
          const dur = 1200;
          const t0 = performance.now();
          const tick = (now) => {
            const p = Math.min((now - t0) / dur, 1);
            const ease = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(start + (target - start) * ease));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }, delay);
      }
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, delay]);

  return <span ref={ref}>{prefix}{val}{suffix}</span>;
}

const TICKS = ['Figma','Node.js','UI/UX Design','Python','CSS Motion','Problem Solver','Suffolk Univ.','Class of 2026','Frontend Dev','Open to Work','FastAPI','Tailwind'];

export default function Hero() {
  return (
    <section className="hero-section">
      <div className="hero-glow hero-glow-1" />
      <div className="hero-glow hero-glow-2" />

      <div className="container">
        <div className="hero-grid">
          {/* LEFT */}
          <div className="hero-left">
            <div className="status-bar">
              <div className="pulse-dot" />
              <span className="status-text">available for opportunities · class of 2026</span>
            </div>

            <h1 className="hero-name">
              <span className="name-1">Kelechi</span>
              <span className="name-2">Onwuka</span>
            </h1>

            <div className="hero-role">
              <TypeWriter strings={ROLES} />
            </div>

            <p className="hero-bio">
              I build things I actually need, and share them in case you do too.
              Computer Science @ Suffolk University.
            </p>

            <div className="hero-btns">
              <button
                className="btn-primary"
                onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 10L10 2M10 2H4M10 2V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                View Projects
              </button>
              <a
                href="/Kelechi_Onwuka_Resume_2026.pdf"
                className="btn-secondary"
                target="_blank"
                rel="noreferrer"
              >
                View Résumé
              </a>
            </div>

            <div className="hero-tags">
              {['frontend','ui/ux','product-minded'].map(t => (
                <span key={t} className="hero-tag">{t}</span>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="hero-right">
            <LiveCard />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat">
          <div className="stat-num"><CountUp target={5} suffix="+" /></div>
          <div className="stat-label">projects built</div>
        </div>
        <div className="stat">
          <div className="stat-num"><CountUp target={10} suffix="+" delay={100} /></div>
          <div className="stat-label">technologies</div>
        </div>
        <div className="stat">
          <div className="stat-num"><CountUp target={2026} delay={200} /></div>
          <div className="stat-label">graduating</div>
        </div>
      </div>

      {/* Ticker */}
      <div className="ticker">
        <div className="ticker-inner">
          {[...TICKS, ...TICKS].map((t, i) => (
            <span key={i} className={`tick ${i % 3 === 0 ? 'tick-hi' : ''}`}>{t}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

// Weather icon components with animations
function WeatherIcon({ condition }) {
  if (condition === 'Clear') return (
    <div className="weather-icon sun">
      <div className="sun-core" />
      {[...Array(8)].map((_, i) => (
        <div key={i} className="sun-ray" style={{ transform: `rotate(${i * 45}deg)` }} />
      ))}
    </div>
  );
  if (condition === 'Rain' || condition === 'Drizzle') return (
    <div className="weather-icon rain">
      <div className="cloud-body" />
      <div className="raindrops">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="raindrop" style={{ animationDelay: `${i * 0.15}s`, left: `${i * 18}%` }} />
        ))}
      </div>
    </div>
  );
  if (condition === 'Snow') return (
    <div className="weather-icon snow">
      <div className="cloud-body" />
      <div className="snowflakes">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="snowflake" style={{ animationDelay: `${i * 0.2}s`, left: `${i * 18}%` }}>❄</div>
        ))}
      </div>
    </div>
  );
  if (condition === 'Thunderstorm') return (
    <div className="weather-icon storm">
      <div className="cloud-body dark" />
      <div className="lightning">⚡</div>
    </div>
  );
  // Clouds / Mist / default
  return (
    <div className="weather-icon cloudy">
      <div className="cloud-back" />
      <div className="cloud-front" />
    </div>
  );
}

function LiveCard() {
  const [time, setTime] = useState('');
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const tick = () => {
      const now = new Date().toLocaleTimeString('en-US', {
        timeZone: 'America/New_York',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      });
      setTime(now);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const API_KEY = '0919c80a573f92a8cae18af809f308f1';
    const url = `https://api.openweathermap.org/data/2.5/weather?q=Boston,US&appid=${API_KEY}&units=imperial`;
    fetch(url)
      .then(r => r.json())
      .then(data => {
        if (data.main && data.weather) {
          setWeather({
            temp: Math.round(data.main.temp),
            condition: data.weather[0].main,
            desc: data.weather[0].description,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setWeather({ temp: '--', condition: 'Clouds', desc: 'unavailable' });
        setLoading(false);
      });
  }, []);

  return (
    <div className="terminal-card live-card">
      <div className="terminal-header">
        <span className="dot" style={{ background: '#FF5F57' }} />
        <span className="dot" style={{ background: '#FEBC2E' }} />
        <span className="dot" style={{ background: '#28C840' }} />
        <span className="term-label">current.status</span>
        <span className="live-badge">● LIVE</span>
      </div>
      <div className="term-body live-body">
        <div className="term-line"><span className="tc">// you found me</span></div>
        <div className="live-row">
          <span className="tk">time</span>
          <span className="tv live-time">{time || '--:--:-- AM'}</span>
        </div>
        <div className="live-row">
          <span className="tk">location</span>
          <span className="tv">"Boston, MA"</span>
        </div>
        <div className="live-weather-row">
          <div className="live-weather-text">
            <div className="live-row">
              <span className="tk">weather</span>
              <span className="tv">
                {loading ? 'fetching...' : `${weather?.temp}°F`}
              </span>
            </div>
          </div>
          <div className="live-weather-icon">
            {!loading && weather && <WeatherIcon condition={weather.condition} />}
          </div>
        </div>
        <div className="live-divider" />
        <div className="term-line"><span className="tc">// what i'm up to</span></div>
        <div className="live-row">
          <span className="tk">building</span>
          <span className="tv">"business client website"</span>
        </div>
        <div className="live-row">
          <span className="tk">grad_school</span>
          <span className="tv">"Fall 2026"</span>
        </div>
        <div className="live-row">
          <span className="tk">open_to</span>
          <span className="tv goal-text">"internships + full time"</span>
        </div>
      </div>
    </div>
  );
}

function SkillBar({ name, w }) {
  const ref = useRef(null);
  const barRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && barRef.current) {
        barRef.current.style.width = w + '%';
        obs.disconnect();
      }
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [w]);

  return (
    <div className="skill-item" ref={ref}>
      <div className="skill-name">{name}</div>
      <div className="skill-bar">
        <div className="skill-fill" ref={barRef} />
      </div>
    </div>
  );
}